import { observable, action, toJS } from 'mobx';

import { observation } from '../actions/api';
import _ from 'lodash';

export default class HQCStore {
    @observable pagination_size = 50;

    @observable pagination_index_max = 0;
    @observable pagination_index = 0;

    @observable pinned = false;
    @observable referenceMarker = null;

    @observable distance = 25;
    @observable circle = null;

    @observable isLoading = false;
    @observable editHQC = false;
    @observable editValue = false;

    @observable searchTags = [];
    @observable parameters = [];
    @observable dataSeries = [];
    @observable selectedDataSeries = [];

    @observable date_from = new Date(new Date().setDate(new Date().getDate() - 1));
    @observable date_to = new Date();

    @observable observations = [];
    @observable data = [];
    @observable selectedObservations = [];
    @observable multiTagOptions = [];

    @observable ignore = [];

    @observable groupedData = {}

    getDataInSameLine = () => {
        const dataInSameLine = [];
        this.selectedDataSeries.forEach((data_series, index) => {
            this.data.forEach(item => {
                const obj = item[data_series.id];
                if (!obj) return;
                const existingObjs = dataInSameLine.filter(i => i.time === item.time && i.column_index === index && i.name === obj.name
                    && i.series_id === obj.series_id && i.unit === obj.unit);
                let row_index = existingObjs.length > 0 ? existingObjs[existingObjs.length - 1].row_index + 1 : 0;
                const column_index = index;
                dataInSameLine.push({ ...obj, time: item.time, column_index, row_index });
            });
        })

        dataInSameLine.forEach((item) => {
            if (item.column_index > 0) {
                const sameData = dataInSameLine.filter(i => i.time === item.time && i.column_index <= item.column_index - 1 && i.row_index === item.row_index);
                const diff = item.column_index - sameData.length;
                for (let i = 0; i < diff; i++) {
                    dataInSameLine.push({ column_index: i, value: null, row_index: item.row_index, time: item.time })
                }
            }
        })
        const grouped = _.groupBy(dataInSameLine, 'time');
        const ordered = Object.keys(grouped).sort((a, b) => b - a).reduce(
            (obj, key) => {
                obj[key] = grouped[key];
                return obj;
            },
            {}
        );
        this.groupedData = ordered;
    }

    getParameterById = (id) => {
        return this.parameters.find(i => +i.id === +id);
    };

    filterDataSeries = (dataSeries, filterText) => {
        return dataSeries.filter(data => (
            (!!data &&
                !!data.name &&
                !!data.name.toLowerCase().indexOf(filterText) !== -1) ||

            (!!data &&
                !!data.parameter_id &&
                !!this.getParameterById(data.parameter_id) &&
                this.getParameterById(data.parameter_id).code.toString().toLowerCase().indexOf(filterText) !== -1)
        ))
    };

    @action fetchObservationsBySeriesIdAndStartEndDate = (filters) => {
        this.isLoading = true;

        return observation.fetchObservationsWithQCValues(filters).then(res => {
            this.isLoading = false;

            if (!res.message)
                return [...res];
            else
                return [];
        }).catch(e => {
            console.log(e);
            this.isLoading = false;
        })
    }

    @action firstPage = () => {
        this.pagination_index = 0;
    }

    @action nextPage = () => {
        this.pagination_index += (this.pagination_index + 1 > this.pagination_index_max ? 0 : 1);
    }

    @action previousPage = () => {
        this.pagination_index -= (this.pagination_index === 0 ? 0 : 1);
    }

    @action finalPage = () => {
        this.pagination_index = this.pagination_index_max;
    }

    @action setMultiTagOptions = (multiTagOptions) => {
        this.multiTagOptions = multiTagOptions;
    }

    @action setPinned = (pinned) => {
        this.pinned = pinned;
    }

    @action setSearchTags = (searchTags) => {
        this.searchTags = searchTags;
    }

    @action setEditHQC = (edit) => {
        this.editHQC = edit;
    }

    @action setEditValue = (edit) => {
        this.editValue = edit;
    }

    @action setIgnore = (ignore) => {
        this.ignore = ignore;
    }

    @action setSelectedDataSeries = (selectedDataSeries) => {
        this.selectedDataSeries = selectedDataSeries;
    }

    @action setParameters = (parameters) => {
        this.parameters = parameters;
    }

    @action setDataSeries = (dataSeries) => {
        this.dataSeries = dataSeries;
    }

    @action setObservations = (observations) => {
        this.observations = observations;
    };

    @action setData = (data) => {
        this.data = data;

        this.getDataInSameLine();
        // this.pagination_index = 0;
    };

    @action setCircle = (circle) => {
        this.circle = circle;
    };

    @action setDistance = (distance) => {
        this.distance = distance;
    };

    @action setSelectedObservations = (selectedObservations) => {
        this.selectedObservations = selectedObservations;
    };

    @action setReferenceMarker = (referenceMarker) => {
        this.referenceMarker = referenceMarker;
    }

    @action
    pushToObservations = (entries) => {
        const updatedObservations = [...this.observations];

        entries.forEach(({ series_id, observations }) => {
            const existingSeriesObservations = updatedObservations.find(o => +o.series_id === +series_id);

            if (existingSeriesObservations) {
                updatedObservations.find(o => +o.series_id === +series_id).arr = observations;
            } else {
                updatedObservations.push({ series_id, arr: observations });
            }
        })

        this.observations = updatedObservations;
    };

    getObservations = () => {
        const promises = [];
        const { date_from, date_to, fetchObservationsBySeriesIdAndStartEndDate, selectedDataSeries } = this;

        selectedDataSeries.forEach(item => {
            promises.push(
                fetchObservationsBySeriesIdAndStartEndDate({
                    series_id: item.id,
                    date_from: new Date(new Date(date_from).setHours(0, 0, 0, 0)),
                    date_to: new Date(new Date(date_to).setHours(23, 59, 59, 999))
                }).then(res => {
                    return { observations: res, series_id: item.id };
                })
            )
        });

        return Promise.all(promises).then((responses) => {
            this.pushToObservations(responses);
            this.getCombinedTimeData();
            return Promise.resolve();
        });
    }

    getNewlyAddedObservations = () => {
        const promises = [];
        const { date_from, date_to, fetchObservationsBySeriesIdAndStartEndDate, selectedDataSeries } = this;

        selectedDataSeries.forEach(item => {
            if (!this.observations.find(o => +o.series_id === +item.id))
                promises.push(
                    fetchObservationsBySeriesIdAndStartEndDate({
                        series_id: item.id,
                        date_from: new Date(new Date(date_from).setHours(0, 0, 0, 0)),
                        date_to: new Date(new Date(date_to).setHours(23, 59, 59, 999))
                    }).then(res => {
                        return { observations: res, series_id: item.id };
                    })
                );
        });

        return Promise.all(promises).then((responses) => {
            this.pushToObservations(responses);
            this.getCombinedTimeData();
            return Promise.resolve();
        });
    }

    @action
    getCombinedTimeData = () => {
        const newData = [];

        this.observations.forEach(o => {
            if (o && o.arr)
                o.arr.forEach(item => {
                    newData.push({
                        time: new Date(item.time).getTime(),
                        [o.series_id]: { ...item }
                    });

                });
        });
        this.data = newData.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
        this.pagination_index_max = Math.floor(newData.length / this.pagination_size);
        this.getDataInSameLine();
    }

    @action
    handleFromChange = (e) => {
        this.date_from = e.target.value;
    };

    @action
    handleToChange = (e) => {
        this.date_to = e.target.value;
    };
}



// WEBPACK FOOTER //
// ./src/stores/HQCStore.js